<script setup lang="ts">
import { useEcommerceOnboarding } from '~/composables/useEcommerceOnboarding'
import { excludedRoutesBottomNavigation } from '~/lib/models/ui/bottom-navigation'
import { getValueOfVersionTyC } from '~/utils/favoriteUtils'

const site = useSite()
const isMobile = useIsMobile()
const isTablet = useIsTablet()
useLinkHandler()
const restaurantStore = useRestaurantStore()
const appStore = useAppStore()
const authStore = useAuthStore()
const layoutStore = useLayoutStore()
const { init: initEcommerceOnboarding } = useEcommerceOnboarding()

const checkingTermsFor = ref<string>()
const openMandatoryTermsModal = ref(false)

provide('showOverlay', layoutStore.setLoaderOverlay)

const isRestaurantSelected = computed(() => restaurantStore.isRestaurantSelected)

const isLandingPage = computed(() => {
  const route = useRoute()
  return route.path.startsWith('/landing')
})

const route = useRoute()

const isFaqsDuplicatesPage = computed(() => route.path === '/faqs-duplicates')

const showHeader = computed(() => {
  // No mostrar el header en las páginas de landing
  if (isLandingPage.value) return false
  if (isFaqsDuplicatesPage.value) return false

  const excludedRoutesHeader = [
    'my-account',
    'my-account-my-orders',
    'my-account-sectionSlug',
    'my-account-sectionSlug-my-devices',
    'restaurants-citySlug-restaurantSlug',
    'faqs',
    'faqs-question',
  ]
  const route = useRoute()
  const routeName = route.name ? (route.name as string) : undefined

  if (isMobile.value) {
    // show on landing home
    if (route.path === '/') return true
    else if (routeName && excludedRoutesHeader.includes(routeName)) return false
  }
  if (site.header && !isMobile.value) return true
  return showBottomNavigationBar.value
})

const showBottomNavigationBar = computed(() => {
  // No mostrar el header en las páginas de landing
  if (isLandingPage.value) return false
  if (isFaqsDuplicatesPage.value) return false

  const route = useRoute()
  const routeName = route.name ? (route.name as string) : undefined
  if (isMobile.value) {
    // hide on landing home
    if (route.path === '/') return false
    if (routeName && excludedRoutesBottomNavigation.includes(routeName)) return false
    return true
  } else {
    return false
  }
})

const showFloatingCartBag = computed(() => {
  if (appStore.cart.products.length === 0) return false
  if (!showHeader.value) return false

  return showBottomNavigationBar.value
})

const showFooter = computed(() => {
  // No mostrar el header en las páginas de landing
  if (isLandingPage.value) return false
  if (isFaqsDuplicatesPage.value) return false

  const excludedRoutesFooter = [
    'my-account',
    'my-account-my-orders',
    'my-account-sectionSlug',
    'my-account-sectionSlug-my-devices',
  ]

  const route = useRoute()
  const routeName = route.name ? (route.name as string) : undefined

  if (isMobile.value) {
    if (routeName && excludedRoutesFooter.includes(routeName)) return false
    return true
  }

  if (site.footer && !isMobile.value) {
    return true
  } else {
    return false
  }
})

const checkMandatoryTermsMustBeShowed = () => {
  // Prevent showing the mandatory terms modal on landing pages (e.g. /landing/minecraft, /landing/ticketbox)
  if (isLandingPage.value) return

  if (
    !authStore.user ||
    !authStore.isUserLogged ||
    !appStore.getMandatoryTermsEnabled ||
    !appStore.mdwConfig?.versionTyC
  )
    return

  const userMetadata = authStore.userMetadata
  let mustShowModal = false

  if (!userMetadata?.versionTyc) mustShowModal = true
  else {
    const country = appStore.countryCode
    const userVersionByCountry = userMetadata.versionTyc
    const valueOfVersionTyC = getValueOfVersionTyC(userVersionByCountry, country) ?? 0
    const countryVersionTyC = parseInt(appStore.mdwConfig.versionTyC) || 0

    if (valueOfVersionTyC < countryVersionTyC) {
      mustShowModal = true
    }
  }

  if (mustShowModal) {
    openMandatoryTermsModal.value = true
  }
}

const onAcceptTerms = async () => {
  openMandatoryTermsModal.value = false
  const country = appStore.countryCode
  const countryVersionTyC = appStore.mdwConfig?.versionTyC
    ? parseInt(appStore.mdwConfig.versionTyC) || 0
    : 0

  const versionTycMetadataUpdated = updateValueOfVersionTyC({
    versionTyc: authStore.userMetadata?.versionTyc,
    newVersionTyc: countryVersionTyC,
    country,
  })
  try {
    await authStore.updateUser(
      {
        versionTyc: versionTycMetadataUpdated,
      },
      false
    )
  } catch (error) {
    //
  }
}

provide('isRestaurantSelected', isRestaurantSelected)
provide('isMobile', isMobile)
provide('isTablet', isTablet)

watch(
  () => layoutStore.onboarding,
  () => {
    if (layoutStore.onboarding) initEcommerceOnboarding()
  }
)

watchEffect(() => {
  if (authStore.user && checkingTermsFor.value !== authStore.user.id) {
    checkingTermsFor.value = authStore.user.id
    checkMandatoryTermsMustBeShowed()
  }
})
</script>

<template>
  <div class="relative" :class="{ 'mb-20': showBottomNavigationBar && !isLandingPage }">
    <SectionHeader v-if="showHeader" critical />
    <main
      id="main-content"
      :class="{ 'min-h-screen': isLandingPage, 'min-h-[80svh] md:min-h-[75svh]': !isLandingPage }"
    >
      <slot />
    </main>
    <SectionFooter v-if="showFooter && showHeader" critical />
    <BottomNavigationBar :show-navigation-bar="showBottomNavigationBar" />
    <FloatingCartBag v-if="showFloatingCartBag" />
    <ToastContainer />
    <LazyMenuSideBar />
    <LazyModalSelectRestaurant
      v-if="
        restaurantStore.modalMOP ||
        restaurantStore.modalDLV ||
        restaurantStore.modalChangeRestaurant
      "
    />
    <DynamicDialog />
    <LazyPickupMethodsView v-if="appStore.pickupMethodModal" />
    <LazyRateOrderView v-if="appStore.ratingIntentData" />
    <LazyMandatoryTermsModal
      v-if="openMandatoryTermsModal"
      @close="openMandatoryTermsModal = false"
      @accept="onAcceptTerms"
    />
  </div>
  <LoaderOverlay v-if="layoutStore.loaderOverlay" />
</template>

<style>
/* Styles for DynamicDialog */
.p-dialog-mask {
  @apply !items-end md:!items-center;
}
.p-dialog-mask .p-dialog {
  @apply m-0 md:m-4;
}
</style>
